import {
  FAILED,
  LAYER_PROP_SUCCESS,
  LAYERS_PROP_FAIL,
  LAYERS_PROP_SUCCESS,
  SUCCESS,
} from '@App/app/configs/toastr-events.config';
import {
  SUCCESS_TOASTR_CONFIG,
  WARNING_TOASTR_CONFIG,
} from '@App/app/configs/toastr-messages.config';
import { LayerUtilsService } from '@App/app/engine/services/layer-services/layer-utils-service/layer-utils.service';
import { ViewerLayerService } from '@App/app/engine/services/layer-services/viewer-layer-service/viewer-layer.service';
import { LAYER_TYPES } from '@App/app/entities/layer/enums/layer-types.enum';
import { LayerUI } from '@App/app/entities/layer/layer-ui.model';
import { httpLayer } from '@App/app/entities/layer/layer.model';
import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { LayerHttpService } from '../../../../engine/services/layer-services/layer-http-service/layer-http.service';
import { LayersService } from '../../services/layers/layers.service';
import {
  changeLayerProp,
  changeLayerPropSuccess,
  changeLayersProp,
  changeLayersPropSuccess,
  updateLayer,
  updateLayers,
} from '../actions/layers.actions';
import { selectAllLayers } from '../selectors/layers.selectors';

@Injectable()
export class ChangeLayerPropEffects {
  constructor(
    private actions: Actions,
    private layerHttpService: LayerHttpService,
    private layerUtilsService: LayerUtilsService,
    private toastrService: NbToastrService,
    private layersService: LayersService,
    private viewerLayerService: ViewerLayerService,
    private store: Store,
  ) {}

  changeLayerProp = createEffect(() => {
    return this.actions.pipe(
      ofType(changeLayerProp),
      withLatestFrom(this.store.select(selectAllLayers)),
      mergeMap(([{ id, prop }, layers]) => {
        const body = this.buildPropBody(id, prop, layers);
        if (!body) {
          return EMPTY;
        }
        return this.layerHttpService.putBatchLayers([body]).pipe(
          map(([layer]) => changeLayerPropSuccess({ layer, key: prop.key })),
          catchError(() => EMPTY),
        );
      }),
    );
  });

  changeLayerPropSuccess = createEffect(() => {
    return this.actions.pipe(
      ofType(changeLayerPropSuccess),
      withLatestFrom(this.layersService.loadingLayers$),
      map(([{ layer, key }, loadingLayers]) => {
        this.layersService.setLoadingLayers(loadingLayers.filter((item) => item.id !== layer.id));
        this.toastrService.show(LAYER_PROP_SUCCESS, SUCCESS, {
          ...SUCCESS_TOASTR_CONFIG,
        });
        if (key === 'name' && layer.type !== LAYER_TYPES.FOLDERS) {
          this.viewerLayerService.changeLayerViewerName(layer.id, layer.name);
        }
        return updateLayer({
          update: {
            id: layer.id,
            changes: { data: layer.data, [key]: layer[key] },
          },
        });
      }),
    );
  });

  changeLayersProp = createEffect(() => {
    return this.actions.pipe(
      ofType(changeLayersProp),
      withLatestFrom(this.store.select(selectAllLayers)),
      mergeMap(([{ ids, prop }, layers]) => {
        this.layersService.setLoading(true);
        const data = ids
          .map((id) => this.buildPropBody(id, prop, layers))
          .filter((i): i is httpLayer => !!i);
        return this.layerHttpService.putBatchLayers(data).pipe(
          map((res) => {
            return changeLayersPropSuccess({
              layers: res,
              key: prop.key,
            });
          }),
          catchError(() => {
            this.layersService.setLoading(false);
            this.toastrService.show(LAYERS_PROP_FAIL, FAILED, {
              ...WARNING_TOASTR_CONFIG,
            });
            return EMPTY;
          }),
        );
      }),
    );
  });

  changeLayersPropSuccess = createEffect(() => {
    return this.actions.pipe(
      ofType(changeLayersPropSuccess),
      map(({ layers, key }) => {
        this.layersService.setLoading(false);
        this.toastrService.show(LAYERS_PROP_SUCCESS, SUCCESS, {
          ...SUCCESS_TOASTR_CONFIG,
        });
        const updates = layers.map((layer) => {
          return {
            id: layer.id,
            changes: { data: layer.data, [key]: layer[key] },
          };
        });
        return updateLayers({ updates });
      }),
    );
  });

  private buildPropBody(
    id: number,
    prop: { key: string; value: string | number | boolean },
    layers: LayerUI[],
  ): httpLayer | null {
    const layerToUpdate = layers.find((layer) => layer.id === id);
    if (!layerToUpdate) {
      return null;
    }
    const body = this.layerUtilsService.createRequestBody(layerToUpdate);
    body.data = layerToUpdate.data;
    (body[prop.key as keyof httpLayer] as typeof prop.value) = prop.value;
    return body;
  }
}
